/* Sprint 20 — Reusable searchable creatable combobox.
   - Predefined options + recent (from localStorage) shown first.
   - Free-text input allowed: hitting Enter on a non-matching value creates it.
   - Full keyboard support (arrows, Enter, Escape); Tab exits naturally.
   - Meant for Fahrzeugmarke, Modell, Farbe. */

import { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Check, ChevronsUpDown, Plus, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

export function ComboBoxCreatable({
  value,
  onChange,
  options,             // [{ value, recent }]
  placeholder = "",
  emptyLabel = "Keine Treffer",
  createLabel = "Übernehmen",
  testId = "combobox",
  onCommit,            // called when a value is committed (Enter or select)
  renderValue,         // optional: (value) => ReactNode for the trigger label
  renderOption,        // optional: (option) => ReactNode for each row body
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { if (!open) setQuery(""); }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.value.toLowerCase().includes(q));
  }, [options, query]);

  const exactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q && options.some((o) => o.value.toLowerCase() === q);
  }, [options, query]);

  const commit = (v) => {
    onChange(v);
    onCommit?.(v);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors",
            "focus:outline-none focus:ring-1 focus:ring-ring",
          )}
        >
          <span className={cn("truncate text-left", !value && "text-muted-foreground")}>
            {value ? (renderValue ? renderValue(value) : value) : placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-2">
            <Input
              ref={inputRef}
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Suchen oder eigenen Wert eingeben…"
              className="h-9 border-0 shadow-none focus-visible:ring-0"
              data-testid={`${testId}-search`}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const q = query.trim();
                  // If there's a filtered match, cmdk will handle it via CommandItem.
                  // Fallback: create the custom value when no exact match.
                  if (q && !exactMatch && filtered.length === 0) {
                    commit(q);
                  } else if (q && !exactMatch && filtered.length > 0) {
                    // Prefer first filtered match on Enter
                    commit(filtered[0].value);
                  } else if (q && exactMatch) {
                    commit(q);
                  }
                }
              }}
            />
          </div>
          <CommandList
            ref={listRef}
            className="h-64 max-h-[45vh] overflow-y-scroll overscroll-contain touch-pan-y"
            style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y", overscrollBehavior: "contain" }}
            onWheelCapture={(event) => event.stopPropagation()}
            onTouchMoveCapture={(event) => event.stopPropagation()}
            data-testid={`${testId}-options`}
          >
            {filtered.length === 0 && !query && (
              <CommandEmpty>{emptyLabel}</CommandEmpty>
            )}
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((o) => (
                  <CommandItem
                    key={o.value}
                    value={o.value}
                    onSelect={() => commit(o.value)}
                    data-testid={`${testId}-option-${o.value}`}
                  >
                    {o.recent ? (
                      <Clock className="mr-2 h-3.5 w-3.5 text-primary" />
                    ) : (
                      <span className="mr-2 inline-block h-3.5 w-3.5" />
                    )}
                    <span className="flex-1 truncate">{renderOption ? renderOption(o) : o.value}</span>
                    {value === o.value && <Check className="ml-2 h-3.5 w-3.5 text-primary" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {query && !exactMatch && (
              <CommandGroup heading="Eigener Wert">
                <CommandItem
                  value={`__create__${query}`}
                  onSelect={() => commit(query.trim())}
                  data-testid={`${testId}-create`}
                >
                  <Plus className="mr-2 h-3.5 w-3.5 text-primary" />
                  {createLabel}: <span className="ml-1 font-medium">{query.trim()}</span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
